import React, { useState } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import { ShieldOff, UploadCloud } from "lucide-react";
import { usePrivilege } from "../../../guard/usePrivilege";
import { getOrganizationDataFromStorage } from "../../../utils/officeCodeContext";

const TENANTS = ["ADSC", "CAD", "DIAMOND", "Pawsitively-RIP"];

// Test-only utility to upload an EDI 850 order XML and trigger order creation without
// needing Swagger. Gated by the same de-facto admin privilege used elsewhere in this app.
// Hits the real /api/edi/process endpoint — no separate backend path, no changed security model.
const UploadOrderXmlTest = () => {
    const { hasPrivilege } = usePrivilege();
    const canUse = hasPrivilege("shipments:ADMIN_VIEW");

    const officeOptions = getOrganizationDataFromStorage()
        .map((org) => ({
            officeCode: org.officeCode,
            label: org.organizationName || org.officeName || org.name || org.orgName || org.officeCode,
        }))
        .filter((o) => o.officeCode);

    const [officeCode, setOfficeCode] = useState(
        Cookies.get("officeCode") || officeOptions[0]?.officeCode || ""
    );
    const [tenant, setTenant] = useState(TENANTS[0]);
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    if (!canUse) {
        return (
            <div className="w-full h-full flex items-center justify-center p-fluid-2">
                <div className="flex flex-col items-center text-center gap-3 max-w-md">
                    <ShieldOff className="w-10 h-10 text-red-500" />
                    <h2 className="text-lg font-bold text-red-600">Access Restricted</h2>
                    <p className="text-sm text-gray-600 dark:text-acs-dark-subtext">
                        This is a test-only tool for uploading EDI order files and requires admin access.
                    </p>
                </div>
            </div>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file || !officeCode) return;
        setLoading(true);
        setError("");
        setResult(null);
        try {
            const formData = new FormData();
            formData.append("file", file);
            const token = Cookies.get("authToken");
            const resp = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL || ""}/api/edi/process/${tenant}/office/${officeCode}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            setResult(resp.data);
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                (typeof err?.response?.data === "string" ? err.response.data : null) ||
                err?.message ||
                "Upload failed"
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full h-full overflow-y-auto p-fluid-2 bg-white dark:bg-acs-dark-surface">
            <div className="max-w-xl mx-auto">
                <div className="flex items-center gap-2 mb-2">
                    <UploadCloud className="w-6 h-6 text-[#223688]" />
                    <h2 className="text-fluid-xl font-semibold text-gray-800 dark:text-acs-dark-text">
                        Upload Order XML (Testing Only)
                    </h2>
                </div>
                <p className="text-sm text-gray-500 dark:text-acs-dark-subtext mb-6">
                    Uploads directly to the real EDI ingestion endpoint used by trading partners — for
                    testing order creation from an EDI 850 file without Swagger.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block font-medium mb-1 dark:text-acs-dark-text">Office Code</label>
                        {officeOptions.length > 0 ? (
                            <select
                                value={officeCode}
                                onChange={(e) => setOfficeCode(e.target.value)}
                                required
                                className="border rounded-md p-2 w-full dark:bg-acs-dark-surface dark:text-acs-dark-text dark:border-acs-dark-border"
                            >
                                {!officeOptions.some((o) => o.officeCode === officeCode) && officeCode && (
                                    <option value={officeCode}>{officeCode}</option>
                                )}
                                {officeOptions.map((o) => (
                                    <option key={o.officeCode} value={o.officeCode}>
                                        {o.label} ({o.officeCode})
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <input
                                type="text"
                                value={officeCode}
                                onChange={(e) => setOfficeCode(e.target.value)}
                                required
                                className="border rounded-md p-2 w-full dark:bg-acs-dark-surface dark:text-acs-dark-text dark:border-acs-dark-border"
                                placeholder="e.g. ACME-HQ"
                            />
                        )}
                    </div>
                    <div>
                        <label className="block font-medium mb-1 dark:text-acs-dark-text">
                            Tenant (EDI schema mapping)
                        </label>
                        <select
                            value={tenant}
                            onChange={(e) => setTenant(e.target.value)}
                            className="border rounded-md p-2 w-full dark:bg-acs-dark-surface dark:text-acs-dark-text dark:border-acs-dark-border"
                        >
                            {TENANTS.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block font-medium mb-1 dark:text-acs-dark-text">EDI XML File</label>
                        <input
                            type="file"
                            accept=".xml"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            required
                            className="border rounded-md p-2 w-full dark:bg-acs-dark-surface dark:text-acs-dark-text dark:border-acs-dark-border"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading || !file || !officeCode}
                        className="px-6 py-2 bg-[#1e2a74] text-white hover:bg-[#18225c] rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? "Uploading..." : "Upload & Process"}
                    </button>
                </form>

                {error && (
                    <div className="mt-6 text-red-600 text-sm bg-red-50 dark:bg-red-950/40 p-3 rounded-md whitespace-pre-wrap">
                        <strong>Error:</strong> {error}
                    </div>
                )}

                {result && (
                    <div className="mt-6 text-green-700 dark:text-green-400 text-sm bg-green-50 dark:bg-green-950/40 p-3 rounded-md">
                        <strong>Success</strong>
                        <pre className="mt-2 text-xs overflow-x-auto whitespace-pre-wrap">
                            {JSON.stringify(result, null, 2)}
                        </pre>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UploadOrderXmlTest;
